import ApiFilter from "../Api/ApiFilter";
import AppResource from "./AppResource";
import AppResourceSearch from "./AppResourceSearch";
import Engine from "../Engine";
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import EventEmitter from 'event-emitter-es6'

class AppResourceCollection {

    constructor(resourceType) {
        this._resourceType = resourceType;
        const appResourceClass = Engine.appResourceClass(resourceType);
        this._coreAppResource = new appResourceClass({
            resourceType: resourceType
        });
        this._appResources = {};
        this._emitter = new EventEmitter();
    }

    /**
     * @returns {number}
     */
    get length() {
        return Object.keys(this._appResources).length;
    }

    /**
     * @returns {boolean}
     */
    get isEmpty() {
        return !this.length;
    }

    /**
     * @returns {boolean}
     */
    get isNotEmpty() {
        return !this.isEmpty;
    }

    /**
     * @returns {string}
     */
    get resourceType() {
        return this._resourceType;
    }

    /**
     * @returns {boolean}
     */
    get isDirty() {
        return !isEmpty(this.dirtyResources);
    }

    /**
     *
     */
    get dirtyResources() {
        return keyBy(
            filter(this.appResources, (appResource, uuid) => {
                return appResource.isDirty();
            }),
            'uuid'
        );
    }

    get newResources() {
        return filter(this.appResources, ['exists', false]);
    }

    /**
     *
     * @returns {{}|*}
     */
    get appResources() {
        return this._appResources;
    }

    /**
     *
     * @param value
     */
    set appResources(value) {
        this._appResources = {};
        if (!Array.isArray(value) && typeof value !== 'object') {
            return;
        }

        if (value instanceof AppResource) {
            this.addResource(value);
            return;
        }

        forEach(value, (appResource, uuid) => {
            this.addResource(appResource);
        });
    }

    /**
     * @returns {boolean}
     */
    isResourceDirty(resourceUuid) {
        return !this.dirtyResources.hasOwnProperty(resourceUuid);
    }

    /**
     * @param appResource
     */
    addResource(appResource) {
        if (Array.isArray(appResource)) {
            forEach(appResource, (resource) => {
                this.addResource(resource);
            });
            return;
        }

        if (
            typeof appResource !== 'object' ||
            !(appResource instanceof AppResource)
        ) {
            return;
        }

        const uuid = appResource.exists ? appResource.uuid : Math.random().toString(36).substr(2);
        if (!appResource.exists) {
            appResource.once('created', (resource, apiResponse) => {
                this.forget(uuid);
                this.push(resource);
            });
        }
        this._appResources[uuid] = appResource;
    }

    /**
     * @param uuid
     * @returns {boolean}
     */
    hasResource(uuid) {
        return this._appResources.hasOwnProperty(uuid);
    }

    /**
     * @param uuid
     * @returns {boolean}
     */
    forget(uuid) {
        if (!this.has(uuid)) {
            return;
        }
        delete this._appResources[uuid];
    }

    /**
     * @param uuid
     * @returns {boolean}
     */
    has(uuid) {
        return this.hasResource(uuid);
    }

    /**
     * @param uuid
     * @returns {null|AppResource}
     */
    getResource(uuid) {
        if (!this.has(uuid)) {
            return null
        }
        return this._appResources[uuid];
    }

    /**
     * @param uuid
     * @returns {AppResource}
     */
    get(uuid) {
        return this.getResource(uuid);
    }

    /**
     * @param includingRelations
     */
    refresh(includingRelations = true) {
        forEach(this._appResources, (appResource, uuid) => {
            appResource.refresh(includingRelations);
        });
    }

    /**
     * @param relations
     * @returns {Promise<AppResourceCollection>}
     */
    async load(relations) {
        if (typeof relations === "string") {
            relations = [relations];
        }
        if (!Array.isArray(relations)) {
            relations = [];
        }
        if (!relations.length) {
            return Promise.resolve(this);
        }

        const resourceSearch = AppResourceSearch(this.resourceType);
        resourceSearch.pageSize = Object.keys(this._appResources).length;
        resourceSearch.filter = new ApiFilter({
            'params': {
                'uuid': Object.keys(this._appResources)
            }
        });
        resourceSearch.include = relations;
        await resourceSearch.search();

        forEach(this._appResources, (appResource, uuid) => {
            if (!resourceSearch.resources.has(uuid)) {
                return true;
            }
            appResource.syncFromRemoteAppResource(resourceSearch.resources.get(uuid));
        });

        return this;
    }

    /**
     * @param appResource
     */
    push(appResource) {
        this.addResource(appResource);
    }

    /**
     * @param callback
     */
    each(callback) {
        this.forEach(callback);
    }

    /**
     * @param callback
     */
    forEach(callback) {
        if (typeof callback !== "function") {
            return;
        }
        forEach(this.appResources, callback);
    }

    /**
     * @param callBackOrAtt
     */
    keyBy(callBackOrAtt) {
        return keyBy(this.appResources, callBackOrAtt);
    }

    /**
     * @param callBackOrData
     */
    filter(callBackOrData) {
        const filtered = filter(this.appResources, callBackOrData);
        const newCollection = new AppResourceCollection(this.resourceType);
        forEach(filtered, (appResource) => {
            newCollection.addResource(appResource);
        });
        return newCollection;
    }

    /**
     * @param callBackOrData
     */
    some(callBackOrData) {
        const filtered = some(this.appResources, callBackOrData);
        const newCollection = new AppResourceCollection(this.resourceType);
        forEach(filtered, (appResource) => {
            newCollection.addResource(appResource);
        });
        return newCollection;
    }

    /**
     * @param property
     * @returns {boolean}
     */
    hasOwnProperty(property) {
        if (this.has(property)) {
            return true;
        }
        return Object.prototype.hasOwnProperty.call(this, property);
    }

    getFirst() {
        for (const item in this.appResources) {
            return this.appResources[item]
        }
    }

    /**
     *
     */
    toArray() {
        const arr = [];
        this.each((appResource) => {
            arr.push(appResource)
        });
        return arr;
    }

    buildData(action, includingRelations = false) {
        let builtData = {
            resources: []
        };
        forEach(this.appResources, appResource => {

            if (action === 'create' || action === 'update') {
                let errors = appResource.validate(false);
                if (!isEmpty(errors)) {
                    if (Engine.debug) {
                        let resourceType = appResource.resourceType;
                        console.warn(
                            'Cannot create invalid ' + resourceType,
                            errors,
                        )
                    }
                    return Promise.resolve(null)
                }
            }

            switch (action) {
                case 'create':
                    builtData.resources.push(appResource.buildCreateData(includingRelations));
                    break;
                case 'update':
                    if (appResource.isDirty()) {
                        let updateData = appResource.buildUpdateData(includingRelations);
                        updateData.uuid = appResource.uuid;
                        builtData.resources.push(updateData);
                    }
                    break;
                default:
                    if (appResource.uuid) {
                        builtData.resources.push({uuid: appResource.uuid});
                    }
                    break;
            }
        });
        return builtData;
    }

    async create(includingRelations = false) {
        if (_.isEmpty(this.newResources)) {
            if (Engine.debug) {
                console.warn('Empty collection');
            }
            return Promise.resolve(null);
        }

        let createData = this.buildData('create', includingRelations);
        if (_.isEmpty(createData.resources)) {
            if (Engine.debug) {
                console.warn('No resources to be created');
            }
            return Promise.resolve(null);
        }

        const url = this._coreAppResource.apiPaths().batchCreate;

        this._emitter.emitSync('beforeBatchCreate', this, createData);
        this._emitter.emitSync('beforeBatchSave', this, createData);

        const apiResponse = await Engine.post(url, createData);
        if (isEmpty(apiResponse.resources)) {
            return Promise.resolve(null);
        }

        this.appResources = apiResponse.resources.appResources;

        this._emitter.emit('batchCreated', this, apiResponse);
        this._emitter.emit('batchSaved', this, apiResponse);
        return apiResponse;
    }

    async update(includingRelations = false) {
        if (_.isEmpty(this.dirtyResources)) {
            if (Engine.debug) {
                console.warn('Clean collection');
            }
            return Promise.resolve(null);
        }

        let createData = this.buildData('update', includingRelations);
        if (_.isEmpty(createData.resources)) {
            if (Engine.debug) {
                console.warn('No resources to be updated');
            }
            return Promise.resolve(null);
        }

        const url = this._coreAppResource.apiPaths().batchUpdate;

        this._emitter.emitSync('beforeBatchUpdate', this, createData);
        this._emitter.emitSync('beforeBatchSave', this, createData);

        const apiResponse = await Engine.post(url, createData);
        if (isEmpty(apiResponse.resources)) {
            return Promise.resolve(null);
        }
        forEach(apiResponse.resources.appResources, (appResource, uuid) => {
            appResource.syncFromRemoteAppResource(appResource);
        });

        this._emitter.emit('batchUpdated', this, apiResponse);
        this._emitter.emit('batchSaved', this, apiResponse);
        return apiResponse;
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async delete() {
        let deleteData = this.buildData('delete');
        if (_.isEmpty(deleteData.resources)) {
            if (Engine.debug) {
                console.warn('No resources to be deleted');
            }
            return Promise.resolve(null);
        }

        const url = this._coreAppResource.apiPaths().batchDelete;

        this._emitter.emitSync('beforeBatchDelete', this);
        const apiResponse = await Engine.post(url, deleteData);
        this._emitter.emit('batchDeleted', this);
        this._appResources = {};
        return apiResponse;
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async restore() {
        let restoreData = this.buildData('restore');
        if (_.isEmpty(restoreData.resources)) {
            if (Engine.debug) {
                console.warn('No resources to be deleted');
            }
            return Promise.resolve(null);
        }

        const url = this._coreAppResource.apiPaths().batchRestore;

        this._emitter.emitSync('beforeBatchRestore', this);
        const apiResponse = await Engine.post(url, restoreData);
        this._emitter.emit('batchRestored', this, apiResponse);
        return apiResponse;
    }

    [Symbol.iterator]() {
        const values = this.toArray()
        const length = this.length
        let current = 0
        const iterator = {
            next() {
                if (current < length) {
                    const value = values[current]
                    current++

                    return {
                        done: false,
                        value: value
                    }
                }

                return {done: true, value: undefined}
            }
        }

        return iterator
    }

    /**
     *  Events
     */

    /**
     * @param eventName
     * @param listener
     */
    on(eventName, listener) {
        this._emitter.on(eventName, listener)
    }
}

export default AppResourceCollection;
